import { Button, Checkbox, FormControlLabel, FormGroup, Grid, Paper, Tab, Tabs, Tooltip } from "@mui/material";
import { AutoCompleteOptions } from "../../../../hooks/useLookupTables";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { formatNumberToMoney, roundTwoDecimals } from "../../../Utils";
import Billing from "./Form/Billing";
import Discount from "./Form/Discount";
import CashFlowOptions from "./Form/CashFlowOptions";
import InstallmentsTable from "./InstallmentsTable";
import { Add, Cancel, CurrencyExchange, Remove, Save } from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import axios from "axios";
import { toastError, toastSuccess } from "../../../assets/customToasts";
import { v4 as uuidv4 } from 'uuid'
import SkipVerificationModal from "./SkipVerificationModal";

type Props = {
  customerIds?: number[]
  next?: () => void
  onClose: () => void
  presetBillings?: BillingsWithCashFlow[]
  presetBundle?: BillingsWithCashFlow[]
  referenceYear?: number | null
  refreshTable?: () => Promise<void>
}

type NumericLabelValues = {
  value: number
  label: string
}

export type Billing = {
  id?: number
  fk_id_bundle?: number
  bundle_application_unique_id?: string
  affiliation: AutoCompleteOptions | null
  defaultBilling: AutoCompleteOptions | null
  paymentMethod: number | null
  issueDate: DateTime | null
  service: AutoCompleteOptions | null
  beneficiaryType: number | null
  beneficiary: AutoCompleteOptions | null
  payerType: number | null
  payer: AutoCompleteOptions | null
  payee: number | null
  referenceYear: number | null
  currency: {
    id: number
    sign: string
  }
  totalBilling: NumericLabelValues
}

export type Discount = {
  type: 'percentage' | 'setValue'
  percentage: NumericLabelValues
  setValue: NumericLabelValues
}

export type CashFlowOptions = {
  mode: 'numberOfBills' | 'setValue'
  numberOfBills: NumericLabelValues
  setValue: NumericLabelValues
  dateOfFirstCashflow: DateTime | null
  remainder: 'first-bill' | 'last-bill' | 'equal'
}

export type CashFlowInstallment = {
  id?: number
  fk_id_billing?: number
  installment_number: number
  due_date: DateTime | null
  paid_date: DateTime | null
  grossValue: NumericLabelValues
  dueValue: NumericLabelValues
  paid_value: number
  fk_id_payment_method: number | null
  comment: string | null
  isModified?: boolean
}

export type BillingsWithCashFlow = {
  billing: Billing
  discount: Discount
  cashFlowOptions: CashFlowOptions
  cashFlowInstallments: CashFlowInstallment[]
}

export default function CashFlow({ customerIds, onClose, presetBillings, presetBundle, referenceYear }: Props) {
  console.log('customerIds', customerIds)
  console.log('presetBillings', presetBillings)
  console.log('presetBundle', presetBundle)
  // Hooks
  const { accounts } = useMsal()
  // General
  const [forms, setForms] = useState<BillingsWithCashFlow[]>(presetBillings || presetBundle || [
    {
      billing: {
        affiliation: null,
        defaultBilling: null,
        paymentMethod: null,
        issueDate: DateTime.utc(),
        service: null,
        beneficiaryType: null,
        beneficiary: null,
        payerType: null,
        payer: null,
        payee: null,
        referenceYear: referenceYear || DateTime.utc().get('year'),
        currency: {
          id: 5,
          sign: 'R$'
        },
        totalBilling: {
          value: 0, // Computed value as number, for the calculations
          label: '' // User input is a string so the user can type anything, in case the user type something invalid, we set the error on the component
        }
      },
      discount: {
        type: 'percentage',
        percentage: {
          value: 0, // Computed value as number, for the calculations
          label: '' // User input is a string so the user can type anything, in case the user type something invalid, we set the error on the component
        },
        setValue: {
          value: 0, // Computed value as number, for the calculations
          label: '' // User input is a string so the user can type anything, in case the user type something invalid, we set the error on the component
        }
      },
      cashFlowOptions: {
        mode: 'numberOfBills',
        numberOfBills: {
          value: 0, // Computed value as number, for the calculations
          label: '' // User input is a string so the user can type anything, in case the user type something invalid, we set the error on the component
        },
        setValue: {
          value: 0, // Computed value as number, for the calculations
          label: '' // User input is a string so the user can type anything, in case the user type something invalid, we set the error on the component
        },
        dateOfFirstCashflow: DateTime.utc(),
        remainder: 'first-bill'
      },
      cashFlowInstallments: []
    }
  ])
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [deletedIds, setDeletedIds] = useState<number[]>([])
  const [disableSaveButton, setDisableSaveButton] = useState(false)
  const [skipVerification, setSkipVerification] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  // Memos
  const selectedForm = useMemo(() => forms[tabIndex], [forms, tabIndex])

  const billingError = useMemo(() => isNaN(forms[tabIndex].billing.totalBilling.value), [forms, tabIndex])
  const discountError = useMemo(() => forms[tabIndex].discount.type === 'percentage' ? isNaN(forms[tabIndex].discount.percentage.value) : isNaN(forms[tabIndex].discount.setValue.value), [forms, tabIndex])
  const cashFlowOptionsError = useMemo(() => forms[tabIndex].cashFlowOptions.mode === 'numberOfBills' ? (isNaN(forms[tabIndex].cashFlowOptions.numberOfBills.value) || forms[tabIndex].cashFlowOptions.numberOfBills.value > 48) : (isNaN(forms[tabIndex].cashFlowOptions.setValue.value) && forms[tabIndex].cashFlowOptions.dateOfFirstCashflow !== null), [forms, tabIndex])

  const isFormSubmittable = useMemo(() => {
    // If there's something wrong in the forms
    if (forms.find((_form) => !_form.billing.affiliation || !_form.billing.service || (!_form.billing.beneficiary && !customerIds?.length) || (!_form.billing.payer && !customerIds?.length) || !_form.billing.payee || isNaN(_form.billing.totalBilling.value))) return false
    // If no cash flow has been created
    if (forms.find((_form) => !_form.cashFlowInstallments.length)) return false
    // If any cash flow has missing information
    if (forms.find((_form) => _form.cashFlowInstallments.find((_installment) => !_installment.due_date || isNaN(_installment.grossValue.value) || isNaN(_installment.dueValue.value) || !_installment.fk_id_payment_method))) return false

    return true
  }, [forms, customerIds])

  const netValue = useMemo(() => {
    // If discount is set to percentage, and there's an input for percentage
    if (
      selectedForm.discount.type === 'percentage' &&
      selectedForm.billing.totalBilling.value &&
      !isNaN(selectedForm.discount.percentage.value) &&
      selectedForm.discount.percentage.value >= 0 &&
      selectedForm.discount.percentage.value <= 100
    ) {
      const netTotalBilling = selectedForm.billing.totalBilling.value * (1 - selectedForm.discount.percentage.value / 100)

      return roundTwoDecimals(netTotalBilling) // Truncating to two decimal places
    }
    // If discount is set to setValue, and there's a set value
    if (
      selectedForm.discount.type === 'setValue' &&
      selectedForm.billing.totalBilling.value &&
      !isNaN(selectedForm.discount.setValue.value) &&
      selectedForm.discount.setValue.value > 0 &&
      selectedForm.discount.setValue.value <= selectedForm.billing.totalBilling.value
    ) {
      const netTotalBilling = selectedForm.billing.totalBilling.value - selectedForm.discount.setValue.value

      return roundTwoDecimals(netTotalBilling) // Truncating to two decimal places
    }

    return NaN
  }, [selectedForm.billing, selectedForm.discount])

  const bundleHasEnrollmentPayedByStudent = useMemo(() => presetBundle?.some(bi => bi.billing.service?.id === 6 && bi.billing.payerType === 2), [presetBundle])

  // HTTP methods and helper functions
  const getBillingsObjects = useCallback((forms: BillingsWithCashFlow[], customerIds?: number[]) => {
    // Without preset customers
    if (!customerIds) return forms.map((form) => ({
      fk_id_service: form.billing.service?.id,
      issue_date: form.billing.issueDate?.toISODate(),
      fk_id_currency: form.billing.currency.id,
      fk_id_payee: form.billing.payee,
      fk_id_default_billing: form.billing.defaultBilling?.id || null,
      fk_id_customer_payer: form.billing.payer?.id,
      fk_id_customer_beneficiary: form.billing.beneficiary?.id,
      fk_id_bundle: form.billing.fk_id_bundle || null,
      bundle_application_unique_id: null,
      fk_id_service_provided_type: null,
      reference_year: form.billing.referenceYear,
      fk_id_affiliation: form.billing.affiliation?.id,
      cash_flow: form.cashFlowInstallments.map((installment) => ({
        installment_number: installment.installment_number,
        due_date: installment.due_date?.toISODate(),
        paid_date: installment.paid_date?.toISODate() || null,
        gross_value: installment.grossValue.value,
        due_value: installment.dueValue.value,
        paid_value: installment.paid_value,
        fk_id_payment_method: installment.fk_id_payment_method,
        comment: installment.comment,
      }))
    }))

    // With preset customers
    const billings = []

    for (const id of customerIds) {
      // Get a uuid for this customer
      const uuid = uuidv4()

      for (const form of forms) {
        billings.push({
          fk_id_service: form.billing.service?.id,
          issue_date: form.billing.issueDate?.toISODate(),
          fk_id_currency: form.billing.currency.id,
          fk_id_payee: form.billing.payee,
          fk_id_default_billing: form.billing.defaultBilling?.id || null,
          fk_id_customer_payer: form.billing.payerType === 2 ? id : form.billing.payer?.id,
          fk_id_customer_beneficiary: id,
          fk_id_bundle: form.billing.fk_id_bundle || null,
          bundle_application_unique_id: uuid,
          fk_id_service_provided_type: null,
          reference_year: form.billing.referenceYear,
          fk_id_affiliation: form.billing.affiliation?.id,
          cash_flow: form.cashFlowInstallments.map((installment) => ({
            installment_number: installment.installment_number,
            due_date: installment.due_date?.toISODate(),
            paid_date: installment.paid_date?.toISODate() || null,
            gross_value: installment.grossValue.value,
            due_value: installment.dueValue.value,
            paid_value: installment.paid_value,
            fk_id_payment_method: installment.fk_id_payment_method,
            comment: installment.comment,
          }))
        })
      }
    }

    return billings
  }, [])

  const deleteInstallments = useCallback(async () => {
    if (!deletedIds.length) return false

    // Variables to keep track success/errors
    var successCount = 0
    var errorCount = 0

    const _toast = toast(`Deleting ${deletedIds.length} installments.`, { toastId: 'toast-delete-installments', isLoading: true })

    const allPromises = deletedIds.map(async (id) => {
      try {
        await axios({
          method: 'DELETE',
          url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/cash-flow/${id}`,
          headers: {
            authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
          }
        })

        successCount++
      } catch (err) {
        console.log(err)

        errorCount++
      }

      toast.update(_toast, { render: `Deleting installments: Success: ${successCount}, Errors: ${errorCount}` })
    })

    await Promise.all(allPromises)

    if (errorCount > 0) {
      toastError(_toast, `${errorCount} installments could not be deleted.`)
      return true
    }

    toastSuccess(_toast, 'Installments deleted.')

    return false
  }, [deletedIds])

  const updateInstallments = useCallback(async () => {
    // Get all installments from billings persisted to the database
    const allInstallments = forms.filter((_) => _.billing.id).flatMap((_) => _.cashFlowInstallments)
    // Get all installments persisted to the database and modified
    const updatedInstallments = allInstallments.filter((_) => _.isModified)

    if (!updatedInstallments.length) return false

    // Variables to keep track success/errors
    var successCount = 0
    var errorCount = 0

    const _toast = toast(`Updating ${updatedInstallments.length} installments.`, { toastId: 'toast-update-installments', isLoading: true })

    const allPromises = updatedInstallments.map(async (installment) => {
      try {
        await axios({
          method: 'PUT',
          url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/cash-flow/${installment.id}`,
          headers: {
            authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
          },
          data: {
            fk_id_billing: installment.fk_id_billing,
            installment_number: installment.installment_number,
            due_date: installment.due_date?.toISODate(),
            paid_date: installment.paid_date?.toISODate() || null,
            gross_value: installment.grossValue.value,
            due_value: installment.dueValue.value,
            paid_value: installment.paid_value,
            fk_id_payment_method: installment.fk_id_payment_method,
            comment: installment.comment,
            updated_by: accounts[0]?.username || 'Cypress testing',
          }
        })

        successCount++
      } catch (err) {
        console.log(err)

        errorCount++
      }

      toast.update(_toast, { render: `Updating installments: Success: ${successCount}, Errors: ${errorCount}` })
    })

    await Promise.all(allPromises)

    if (errorCount > 0) {
      toastError(_toast, `${errorCount} installments could not be updated.`)
      return true
    }

    // If any of the billings has a UUID, is of type enrollment, and has student as payer, run voucher verification
    const billingWithPotentialVouchers = forms.filter(tab => {
      console.log({tab})
      const validation = (tab.cashFlowInstallments.some(ins => ins.isModified))
      && Boolean(tab.billing.bundle_application_unique_id) // Has a UUID
      && tab.billing.service?.id === 6 // Enrollment type
      && tab.billing.payerType === 2 // Student
      console.log({validation})
      return validation
    })
    console.log(billingWithPotentialVouchers)

    const voucherRequestPromises = billingWithPotentialVouchers.map(async billing => {
      try {
        const { data } =  await axios({
          method: 'POST',
          url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/check-multiple-bundle-applications`,
          headers: {
            authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
          },
          data: { uuids: [billing.billing.bundle_application_unique_id] }
        })
        return Object.values(data)[0] as string
      } catch(err) {
        console.log(err)
        return 'Unable to run voucher request verification'
      }
    })

    await Promise.all(voucherRequestPromises)
      .then(onFulfilled => {
        for (const feedback of onFulfilled) {
          toast(`${feedback}`, { autoClose: 10000 })
        }
      })


    toastSuccess(_toast, 'Installments updated.')

    return false
  }, [forms, accounts])

  const createInstallments = useCallback(async () => {
    // Get all installments from billings persisted to the database
    const allInstallments = forms.filter((_) => _.billing.id).flatMap((_) => _.cashFlowInstallments)
    // Get all installments not persisted to the database
    const newInstallments = allInstallments.filter((_) => !_.id)

    if (!newInstallments.length) return false

    // Variables to keep track success/errors
    var successCount = 0
    var errorCount = 0

    const _toast = toast(`Creating ${newInstallments.length} installments.`, { toastId: 'toast-create-installments', isLoading: true })

    const allPromises = newInstallments.map(async (installment) => {
      try {
        await axios({
          method: 'POST',
          url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/cash-flow`,
          headers: {
            authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
          },
          data: {
            fk_id_billing: installment.fk_id_billing,
            installment_number: installment.installment_number,
            due_date: installment.due_date?.toISODate(),
            paid_date: installment.paid_date?.toISODate() || null,
            gross_value: installment.grossValue.value,
            due_value: installment.dueValue.value,
            paid_value: installment.paid_value,
            fk_id_payment_method: installment.fk_id_payment_method,
            comment: installment.comment,
            updated_by: accounts[0]?.username || 'Cypress testing',
          }
        })

        successCount++
      } catch (err) {
        console.log(err)

        errorCount++
      }

      toast.update(_toast, { render: `Creating intallments: Success: ${successCount}, Errors: ${errorCount}` })
    })

    await Promise.all(allPromises)

    if (errorCount > 0) {
      toastError(_toast, `${errorCount} installments could not be created.`)
      return true
    }

    toastSuccess(_toast, 'New installments created.')

    return false
  }, [forms, accounts])

  const saveBillings = useCallback(async () => {
    // Get all billings not persisted to the database
    const newBillings = forms.filter((_) => !_.billing.id)

    if (!newBillings.length) return false

    // Variables to keep track success/errors
    var successCount = 0
    var errorCount = 0

    const _toast = toast('Saving new billings and cash flow.', { toastId: 'toast-save-billings', isLoading: true })

    // If we have preset customers, we need to create all billings and cash flow for all customers in the list
    const billings = getBillingsObjects(newBillings, customerIds)

    const allPromises = billings
      .map(async (billing) => {
        try {
          await axios({
            method: 'POST',
            url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/billings`,
            headers: {
              authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
            },
            data: {
              billing,
              is_bundle: Boolean(presetBundle),
              updated_by: accounts[0]?.username || 'Cypress testing'
            }
          })

          successCount++
        }
        catch (err) {
          console.log(err)

          errorCount++
        }

        toast.update(_toast, { render: `Saving new billings and cash flow: Success: ${successCount}, Errors: ${errorCount}` })
      })

    await Promise.all(allPromises)

    // If we are assigning bundles, notify the backend
    if (presetBundle) {
      const uuidsSet = new Set(billings.map((_) => _.bundle_application_unique_id))

      try {
        await axios({
          method: 'POST',
          url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/request-vouchers`,
          headers: {
            authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
          },
          data: {
            skip_verification: skipVerification,
            uuids: Array.from(uuidsSet)
          }
        })

        toast.update(_toast, { render: 'Request vouchers created.' })
      } catch (err) {
        console.log(err)
        toast.update(_toast, { render: 'Request vouchers could not be created.' })
      }
    }

    if (errorCount > 0) {
      toastError(_toast, `${errorCount} billings and cash flow could not be saved.`)
      return true
    }

    toastSuccess(_toast, 'New billings saved.')

    return false
  }, [forms, accounts, skipVerification])

  const onSave = useCallback(async () => {
    setDisableSaveButton(true)

    // Check if there are installments to delete
    const hasDeleteFails = await deleteInstallments()

    // Check if there are installments to update
    const hasUpdateFails = await updateInstallments()

    // Check if there are installments to create
    const hasCreateFails = await createInstallments()

    // New Billings
    const hasSaveFails = await saveBillings()

    setDisableSaveButton(false)

    // If none of the checks failed, close the modal
    if (!hasSaveFails && !hasCreateFails && !hasUpdateFails && !hasDeleteFails) onClose()
  }, [setDisableSaveButton, deleteInstallments, updateInstallments, createInstallments, saveBillings, onClose])

  // Billings operation
  const newBilling = useCallback(() => {
    // Create a new billing in the arrays
    setForms((prev) => prev.concat({
      billing: {
        affiliation: null,
        defaultBilling: null,
        paymentMethod: null,
        issueDate: DateTime.utc(),
        service: null,
        beneficiaryType: null,
        beneficiary: null,
        payerType: null,
        payer: null,
        payee: null,
        referenceYear: referenceYear || DateTime.utc().get('year'),
        currency: {
          id: 5,
          sign: 'R$'
        },
        totalBilling: {
          value: 0,
          label: ''
        }
      },
      discount: {
        type: 'percentage',
        percentage: {
          value: 0,
          label: ''
        },
        setValue: {
          value: 0,
          label: ''
        }
      },
      cashFlowOptions: {
        mode: 'numberOfBills',
        numberOfBills: {
          value: 0,
          label: ''
        },
        setValue: {
          value: 0,
          label: ''
        },
        dateOfFirstCashflow: DateTime.utc(),
        remainder: 'first-bill'
      },
      cashFlowInstallments: []
    }))

    // In this set state, forms is not updated, meaning it's with the old value
    // This is why setting forms.length works
    setTabIndex(forms.length)
  }, [forms, referenceYear])

  const removeBilling = useCallback(() => {
    setTabIndex((prev) => prev + 1 >= forms.length ? prev - 1 : prev)

    setForms((prev) => prev.filter((_, index) => index !== tabIndex))
  }, [forms, tabIndex, setForms, setTabIndex])

  // Upper buttons
  const resetDueValues = useCallback(() => {
    setForms((prev) => {
      const _prev = [...prev]
      const _discount = { ...prev[tabIndex].discount }
      const _installments = [...prev[tabIndex].cashFlowInstallments]

      _prev[tabIndex].cashFlowInstallments = _installments.map((_) => {
        const _dueValue = _discount.type === 'setValue' ? roundTwoDecimals(_.grossValue.value - (_discount.setValue.value / _installments.length)) : _.grossValue.value * (1 - _discount.percentage.value / 100)

        return {
          ..._,
          dueValue: {
            value: _dueValue,
            label: formatNumberToMoney(_dueValue)
          }
        }
      })

      return _prev
    })
  }, [setForms, tabIndex])

  const addInstallment = useCallback(() => {
    setForms((prev) => {
      const _prev = [...prev]
      const _installments = [...prev[tabIndex].cashFlowInstallments]

      const _newInstallment: CashFlowInstallment = {
        installment_number: _installments.length + 1,
        comment: null,
        due_date: null,
        dueValue: {
          value: 0,
          label: '',
        },
        fk_id_payment_method: 2,
        grossValue: {
          value: 0,
          label: '',
        },
        paid_date: null,
        paid_value: 0,
      }

      // If we are updating a billing, add the installment with the fk_id_billing
      if (presetBillings?.length) _newInstallment.fk_id_billing = presetBillings[tabIndex].billing.id

      _prev[tabIndex].cashFlowInstallments = _installments.concat(_newInstallment)

      return _prev
    })
  }, [presetBillings, setForms, tabIndex])

  // Scoped functions for update
  const updateBilling = useCallback((updates: Partial<Billing>) => {
    setForms((prev) => {
      const _prev = [...prev]

      _prev[tabIndex].billing = { ..._prev[tabIndex].billing, ...updates }

      return _prev
    })
  }, [setForms, tabIndex])

  const updateDiscount = useCallback((updates: Partial<Discount>) => {
    setForms((prev) => {
      const _prev = [...prev]

      _prev[tabIndex].discount = { ..._prev[tabIndex].discount, ...updates }

      return _prev
    })
  }, [setForms, tabIndex])

  const updateCashFlowOptions = useCallback((updates: Partial<CashFlowOptions>) => {
    setForms((prev) => {
      const _prev = [...prev]

      _prev[tabIndex].cashFlowOptions = { ..._prev[tabIndex].cashFlowOptions, ...updates }

      return _prev
    })
  }, [setForms, tabIndex])

  const updateCashFlowInstallments = useCallback((updates: CashFlowInstallment[]) => {
    setForms((prev) => {
      const _prev = [...prev]

      _prev[tabIndex].cashFlowInstallments = updates

      return _prev
    })
  }, [setForms, tabIndex])

  const updateCashFlowInstallmentByIndex = useCallback((index: number, updates: Partial<CashFlowInstallment>) => {
    setForms((prev) => {
      const _prev = [...prev]

      _prev[tabIndex].cashFlowInstallments[index] = { ..._prev[tabIndex].cashFlowInstallments[index], ...updates }
      console.log(_prev[tabIndex].cashFlowInstallments[index])
      // If installment is persisted in database, set the isModified flag
      if (_prev[tabIndex].cashFlowInstallments[index].id) _prev[tabIndex].cashFlowInstallments[index].isModified = true

      return _prev
    })
  }, [setForms, tabIndex])

  const deleteInstallment = useCallback((index: number, id: number | undefined) => {
    if (id) setDeletedIds((ids) => ids.concat(id))

    setForms((prev) => {
      const _prev = [...prev]

      _prev[tabIndex].cashFlowInstallments = _prev[tabIndex].cashFlowInstallments
        .filter((_installment, _index) => _index !== index)
        .map((_installment, index) => ({
          ..._installment,
          installment_number: index + 1
        }))

      return _prev
    })
  }, [setForms, tabIndex])

  return <Grid container>
    <SkipVerificationModal
      open={showConfirmationModal}
      closeModal={() => setShowConfirmationModal(false)}
      proceedButtonAction={onSave}
    />
    <Grid item xs={4}>
      {/* Form */}
      <Paper sx={{ backgroundColor: '#0006' }}>
        <Grid container spacing={.75}>
          {/* Billing */}
          <Billing
            customerIds={customerIds}
            error={billingError}
            form={selectedForm}
            netValue={netValue}
            updateBilling={updateBilling}
            updateCashFlowOptions={updateCashFlowOptions}
          />
          {/* Discount */}
          <Discount
            error={discountError}
            form={selectedForm}
            netValue={netValue}
            updateDiscount={updateDiscount}
          />
          {/* CashFlow Options */}
          <CashFlowOptions
            disableSetCashFlowButton={billingError || discountError || cashFlowOptionsError}
            error={cashFlowOptionsError}
            form={selectedForm}
            netValue={netValue}
            updateCashFlowOptions={updateCashFlowOptions}
            updateCashFlowInstallments={updateCashFlowInstallments}
          />
        </Grid>
      </Paper>
    </Grid>
    <Grid item xs={8}>
      <Grid container>
        <Grid item xs={10}>
          <Tabs
            id='cash-flow-form-tabs'
            value={tabIndex}
            onChange={(e, newValue) => setTabIndex(Number(newValue))}
            variant='scrollable'
            scrollButtons='auto'
          >
            {
              forms.map((_, index) => <Tab
                key={index}
                id={`cash-flow-form-tab-${index}`}
                value={index}
                label={_.billing.defaultBilling?.label || _.billing.service?.label || `Billing ${index + 1}`}
                sx={{ maxWidth: 500 }}
              />)
            }
          </Tabs>
        </Grid>
        <Grid item xs={1}>
          <Tooltip title='Create a new billing'>
            <Button
              id='button-new-billing'
              onClick={newBilling}
              color='secondary'
              disabled={Boolean(presetBillings?.length)}
            >
              <Add color='primary' />
            </Button>
          </Tooltip>
        </Grid>
        <Grid item xs={1}>
          <Tooltip title={`Remove a billing: ${selectedForm.billing.defaultBilling?.label || selectedForm.billing.service?.label || 'Billing ' + (tabIndex + 1)}`}>
            <Button
              id='button-remove-billing'
              onClick={removeBilling}
              color='warning'
              disabled={forms.length <= 1 || Boolean(presetBillings?.length)}
            >
              <Remove color='primary' />
            </Button>
          </Tooltip>
        </Grid>
        {/* Spacer */}
        <Grid item xs={7.5} />
        {/* Buttons */}
        <Grid item xs={3}>
          <Button
            id='button-reset-due-values'
            startIcon={<CurrencyExchange />}
            color='info'
            onClick={resetDueValues}
          >
            Reset due values
          </Button>
        </Grid>
        <Grid item xs={1.5}>
          <Button
            id='button-add-cash-flow'
            startIcon={<Add />}
            onClick={addInstallment}
          >
            Add
          </Button>
        </Grid>
        {/* Installments */}
        <Grid item xs={12} maxHeight={'75vh'}>
          <InstallmentsTable
            form={selectedForm}
            updateCashFlowInstallmentByIndex={updateCashFlowInstallmentByIndex}
            deleteInstallment={deleteInstallment}
          />
        </Grid>
        {/* Switch to skip voucher approval verification and create vouchers directly */}
        <Grid item xs={6} >
          {
            bundleHasEnrollmentPayedByStudent
            &&
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={skipVerification}
                onChange={() => setSkipVerification(prev => !prev)}
              />
              <span style={{ color: skipVerification ? 'white' : '#949494' }} >Skip verification (vouchers will be created automatically)</span>
            </div>
          }
        </Grid>
        {/* Buttons */}
        <Grid item xs={3}>
          <Button
            id='button-cancel'
            color='error'
            startIcon={<Cancel />}
            onClick={onClose}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            id='button-save'
            startIcon={<Save />}
            onClick={skipVerification ? () => setShowConfirmationModal(true) : onSave}
            disabled={disableSaveButton || !isFormSubmittable}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Grid>
  </Grid>
}
